import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  isDocumentCompleted,
  isDocumentFailed,
  isDocumentInProgress,
  normalizeDocumentStatus,
} from '../../services/documents/documentStatusUtils';
import {
  documentDebugError,
  documentDebugLog,
  documentDebugWarn,
} from '../../services/documents/documentDebug';
import { canAccessDocuments } from '../../services/chatbot/chatbotRoles';
import { getCookie } from '../../shared/utils';
import { AppDispatch, RootState, RootStore } from '../../store';
import {
  fetchAllDocuments,
  logAnalyticUploadCreateOnTerminalStatus,
  notifyDocumentCompleted,
  notifyDocumentFailed,
} from '../../store/documents/actionCreators';

const POLL_INTERVAL_MS = 10000;
const LOG_PREFIX = '[DocumentProcessing]';

const DocumentProcessingMonitor = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { dashboardUserInfo } = useSelector(
    (state: RootState) => state.dashboardUser,
  );
  const isPollingRef = useRef(false);

  useEffect(() => {
    if (!canAccessDocuments(dashboardUserInfo)) {
      return;
    }

    const pollUploadedDocuments = async () => {
      if (isPollingRef.current) {
        return;
      }

      const state: RootState = RootStore.getState();
      const pendingDocuments = state.documents.uploadedDocuments.filter(
        (document) => isDocumentInProgress(document.status),
      );

      if (pendingDocuments.length === 0) {
        return;
      }

      const token = getCookie('access_token') || '';
      if (!token) {
        return;
      }

      const customerId = pendingDocuments[0].customerId;
      if (!customerId) {
        return;
      }

      documentDebugLog(LOG_PREFIX, 'Polling status_all', {
        pendingCount: pendingDocuments.length,
        customerId,
      });

      isPollingRef.current = true;

      try {
        await dispatch(fetchAllDocuments(token, customerId, true));

        const updatedState: RootState = RootStore.getState();
        const updatedDocuments = updatedState.documents.uploadedDocuments;

        await Promise.all(
          pendingDocuments.map(async (document) => {
            const updatedDocument = updatedDocuments.find(
              (item) => item.documentId === document.documentId,
            );

            if (!updatedDocument) {
              documentDebugWarn(LOG_PREFIX, 'Document missing from status_all', {
                documentId: document.documentId,
                filename: document.filename,
              });
              return;
            }

            const previousStatus = normalizeDocumentStatus(document.status);
            const nextStatus = normalizeDocumentStatus(updatedDocument.status);

            if (
              isDocumentCompleted(nextStatus) &&
              !isDocumentCompleted(previousStatus)
            ) {
              notifyDocumentCompleted(updatedDocument.filename);
              await logAnalyticUploadCreateOnTerminalStatus(
                updatedDocument,
                token,
              );
              return;
            }

            if (
              isDocumentFailed(nextStatus) &&
              !isDocumentFailed(previousStatus)
            ) {
              notifyDocumentFailed(updatedDocument.filename);
              await logAnalyticUploadCreateOnTerminalStatus(
                updatedDocument,
                token,
              );
            }
          }),
        );
      } catch (error) {
        documentDebugError('Document processing poll failed', error);
      } finally {
        isPollingRef.current = false;
      }
    };

    pollUploadedDocuments();
    const intervalId = window.setInterval(
      pollUploadedDocuments,
      POLL_INTERVAL_MS,
    );

    return () => window.clearInterval(intervalId);
  }, [dashboardUserInfo, dispatch]);

  return null;
};

export default DocumentProcessingMonitor;
